/**
 * Existencias.
 *
 * El **disponible** no es el stock: es lo que hay menos lo reservado para
 * órdenes de trabajo ya planificadas. Enseñar sólo el stock haría prometer
 * material que ya tiene dueño.
 */

import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { quantity } from '../table/format.js';

interface Row {
  id: string;
  product_code: string;
  product_name: string;
  unit: string;
  warehouse_name: string;
  warehouse_kind: string;
  on_hand: string;
  reserved: string;
  available: string;
  minimum: string | null;
  bin_location: string | null;
  below_minimum: boolean;
}

const KIND: Record<string, string> = {
  CENTRAL: 'Central',
  DELEGACION: 'Delegación',
  VEHICULO: 'Furgoneta',
  TALLER: 'Taller',
  CLIENTE: 'Cliente',
};

const COLUMNS: Column<Row>[] = [
  { id: 'productCode', label: 'Código', render: (r) => r.product_code, sortable: true, width: '8rem' },
  { id: 'productName', label: 'Artículo', render: (r) => r.product_name },
  {
    id: 'warehouseName',
    label: 'Almacén',
    render: (r) => `${r.warehouse_name} · ${KIND[r.warehouse_kind] ?? r.warehouse_kind}`,
    exportValue: (r) => r.warehouse_name,
  },
  { id: 'binLocation', label: 'Ubicación', render: (r) => r.bin_location ?? '—', width: '8rem' },
  {
    id: 'onHand',
    label: 'Existencias',
    numeric: true,
    sortable: true,
    render: (r) => quantity(r.on_hand, r.unit),
    exportValue: (r) => r.on_hand,
  },
  {
    id: 'reserved',
    label: 'Reservado',
    numeric: true,
    render: (r) => quantity(r.reserved, r.unit),
    exportValue: (r) => r.reserved,
  },
  {
    id: 'available',
    label: 'Disponible',
    numeric: true,
    render: (r) =>
      r.below_minimum ? (
        <span className="due due--late">⚠ {quantity(r.available, r.unit)}</span>
      ) : (
        <span className="due">{quantity(r.available, r.unit)}</span>
      ),
    exportValue: (r) => r.available,
  },
  {
    id: 'minimum',
    label: 'Mínimo',
    numeric: true,
    render: (r) => quantity(r.minimum, r.unit),
    exportValue: (r) => r.minimum ?? '',
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'all', label: 'Todas', param: {} },
  { id: 'low', label: 'Bajo mínimo', param: { belowMinimum: 'true' } },
  { id: 'stock', label: 'Con disponible', param: { withStock: 'true' } },
] as const;

export function StockScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/inventory/stock',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Existencias</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'all' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} referencias</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="existencias"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay existencias que coincidan."
      />
    </section>
  );
}
